/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { isEqual } from 'lodash';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import getModernContext from 'js/lib/getModernContext';
import useRouter, { useParams } from 'js/lib/useRouter';

import { Button, Grid, PageGridContainer, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { getManageProgramLearnersLink, getUsersLink } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import {
  EnterpriseAdminPermissionsContext,
  useEnterpriseAdminPermissions,
} from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import ActivityFilterDialog from 'bundles/enterprise-admin-user-filters/components/ActivityFilterDialog';
import ActivityFilterSection from 'bundles/enterprise-admin-user-filters/components/ActivityFilterSection';
import AdditionalFilterSection from 'bundles/enterprise-admin-user-filters/components/AdditionalFilterSection';
import type {
  UserFilterBuilderError,
  UserFilterBuilderErrors,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import ErrorsNotification, {
  validateUserFilter,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import MatchCounter from 'bundles/enterprise-admin-user-filters/components/MatchCounter';
import PrimaryFilterDialog from 'bundles/enterprise-admin-user-filters/components/PrimaryFilterDialog';
import PrimaryFilterSection from 'bundles/enterprise-admin-user-filters/components/PrimaryFilterSection';
import ZeroState from 'bundles/enterprise-admin-user-filters/components/ZeroState';
import type {
  EnrichmentQuery as EnrichmentQueryType,
  EnrichmentQueryVariables,
} from 'bundles/enterprise-admin-user-filters/components/__generated__/EnrichmentQuery';
import { useUserFilter } from 'bundles/enterprise-admin-user-filters/context/userFilterContext';
import type { EnrichedData, SavedAdminFilter } from 'bundles/enterprise-admin-user-filters/types';
import {
  convertEnrichedProductsToFilterOptions,
  createActivityFilter,
  eventuallyAutoFocus,
} from 'bundles/enterprise-admin-user-filters/utils';
import { ConfirmationDialog, Header, Spacer } from 'bundles/enterprise-ui/components';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type UserFilterBuilderQueryParams = {
  mode: 'new-filter' | 'from-context';
  programSlug?: string;
  programId?: string;
  programName?: string;
};

export const getUserFilterBuilderLink = ({
  thirdPartySlug,
  mode,
  programId,
  programSlug,
  programName,
}: { thirdPartySlug: string } & UserFilterBuilderQueryParams) => {
  const base = `/o/${thirdPartySlug}/user-filter-builder?mode=${mode}`;
  return programSlug
    ? `${base}&programId=${programId}&programSlug=${programSlug}&programName=${encodeURIComponent(programName ?? '')}`
    : base;
};

type PropsFromCaller = {};

type PropsFromGraphql = {
  enrichedData?: EnrichedData;
};

type PropsFromGetContext = {
  enableNewLearnersPage: boolean;
};

type Props = PropsFromCaller & PropsFromGraphql & PropsFromGetContext;

const newFilter = { filterId: 'new-filter', filterName: '', filter: {} };

const UserFilterBuilder = ({ enrichedData, enableNewLearnersPage }: Props) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const router = useRouter();
  const { getIsOrganizationDomainAdmin, getAllProgramIdsWithPermission } = useEnterpriseAdminPermissions();
  const { thirdPartySlug } = useParams();
  const { programSlug, programId, programName, mode } = router.location.query as UserFilterBuilderQueryParams;
  const { userFilter: userFilterFromContext, setUserFilter: setUserFilterInContext } = useUserFilter();
  const [userFilter, setUserFilter] = React.useState<SavedAdminFilter>(
    mode === 'new-filter' ? newFilter : userFilterFromContext
  );
  const [primaryFilterDialogOpen, setPrimaryFilterDialogOpen] = React.useState(false);
  const [activityFilterDialogOpen, setActivityFilterDialogOpen] = React.useState(false);
  const [resetFiltersDialogOpen, setResetFiltersDialogOpen] = React.useState(false);
  const [errors, setErrors] = React.useState<UserFilterBuilderErrors>({});
  const [badRequest, setBadRequest] = React.useState(false);
  const hasErrors = Object.keys(errors).length > 0;
  const clearError = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (error: UserFilterBuilderError) => {
      setErrors((currentErrors) => {
        // eslint-disable-next-line no-param-reassign
        delete currentErrors[error];
        return currentErrors;
      });
    },
    [setErrors]
  );
  const type =
    userFilter.filter.primaryFilter?.type === 'MEMBER'
      ? userFilter.filter.primaryFilter?.activityFilter?.type
      : undefined;
  const activityFilterRef = React.useRef<HTMLDivElement | null>(null);
  const pageContent = (
    <PageGridContainer
      maxWidth="lg"
      component={TrackedDiv}
      trackingName="filter_builder"
      withVisibilityTracking={true}
      requireFullyVisible={false}
      trackClicks={false}
    >
      <Grid item xs={12}>
        <Spacer size={48} />
        <Typography variant="h1semibold" component="h1">
          {_t('Filter builder')}
        </Typography>
        <Typography2 component="h3" variant="subtitleMedium">
          {_t('Select filter criteria to identify a specific groups of learners')}
        </Typography2>
        {hasErrors && <ErrorsNotification errors={errors} css={{ marginTop: 'var(--cds-spacing-200)' }} />}
        <Spacer size={48} />
        <PrimaryFilterDialog
          defaultValue={userFilter.filter.primaryFilter?.type}
          open={primaryFilterDialogOpen}
          onClose={() => {
            setPrimaryFilterDialogOpen(false);
          }}
          onSelect={(filter) => {
            let programIdSelection: { values: string[] };
            if (programId) {
              programIdSelection = { values: [programId] };
            } else if (!getIsOrganizationDomainAdmin()) {
              programIdSelection = { values: getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW') };
            }

            setUserFilter((state) => ({
              ...state,
              filter: {
                ...state.filter,
                primaryFilter: {
                  type: filter,
                  programIdSelection,
                },
              },
              enrichedData:
                programId && programName
                  ? {
                      ...enrichedData!,
                      enrichedPrograms: [{ programId, programName }],
                    }
                  : enrichedData,
            }));
            setPrimaryFilterDialogOpen(false);
          }}
        />
        {userFilter.filter.primaryFilter ? (
          <React.Fragment>
            <Typography variant="h2semibold">{_t('Selected filters')}</Typography>
            <Typography2 component="p">{_t('Show learners matching ALL of the specified filter criteria')}</Typography2>
            <Spacer size={12} />
            <PrimaryFilterSection
              errors={errors}
              thirdPartyOrganization={thirdPartyOrganization}
              programName={programName}
              primaryFilter={userFilter.filter.primaryFilter}
              enrichedData={userFilter.enrichedData}
              onChangePrimaryFilter={() => setPrimaryFilterDialogOpen(true)}
              onPrimaryFilterChange={(change) => {
                setUserFilter((state) => ({
                  ...state,
                  filter: {
                    ...state.filter,
                    primaryFilter: state.filter.primaryFilter && { ...state.filter.primaryFilter, ...change },
                  },
                }));
              }}
              onEnrichedProgramsChange={(enrichedPrograms) => {
                setUserFilter((state) => ({
                  ...state,
                  enrichedData: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.enrichedData!,
                    enrichedPrograms,
                  },
                }));
              }}
              onClearError={clearError}
            />
            {userFilter.filter.primaryFilter && userFilter.filter.primaryFilter.type === 'MEMBER' && (
              <div ref={activityFilterRef} css={{ marginTop: 'var(--cds-spacing-150)' }}>
                <ActivityFilterSection
                  errors={errors}
                  activityFilter={userFilter.filter.primaryFilter.activityFilter}
                  selectedCourses={convertEnrichedProductsToFilterOptions(
                    userFilter.enrichedData,
                    userFilter.filter.primaryFilter.activityFilter?.productIdSelection?.values
                  )}
                  onAddActivityFilter={() => setActivityFilterDialogOpen(true)}
                  onChangeActivityFilter={() => setActivityFilterDialogOpen(true)}
                  onRemoveActivityFilter={() => {
                    setUserFilter((state) => ({
                      ...state,
                      filter: {
                        ...state.filter,
                        primaryFilter: {
                          ...state.filter.primaryFilter!,
                          activityFilter: undefined,
                        },
                      },
                    }));
                  }}
                  onActivityFilterChange={(activityFilter) => {
                    setUserFilter((state) => ({
                      ...state,
                      filter: {
                        ...state.filter,

                        primaryFilter: {
                          ...state.filter.primaryFilter!,
                          activityFilter: {
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion @typescript-eslint/no-non-null-asserted-optional-chain
                            // @ts-expect-error
                            ...state.filter.primaryFilter?.activityFilter!,
                            ...activityFilter,
                          },
                        },
                      },
                    }));
                  }}
                  onEnrichedProductsChange={(enrichedProducts) => {
                    setUserFilter((state) => ({
                      ...state,
                      enrichedData: {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        ...state.enrichedData!,
                        enrichedProducts,
                      },
                    }));
                  }}
                  onClearError={clearError}
                />
              </div>
            )}
            <ActivityFilterDialog
              key={type}
              defaultValue={type}
              open={activityFilterDialogOpen}
              onClose={() => {
                setActivityFilterDialogOpen(false);
              }}
              onSelect={(filterType) => {
                const activityFilter = createActivityFilter(filterType);
                setUserFilter((state) => ({
                  ...state,
                  filter: {
                    ...state.filter,
                    primaryFilter: { ...state.filter.primaryFilter!, activityFilter },
                  },
                }));
                setActivityFilterDialogOpen(false);
                eventuallyAutoFocus(activityFilterRef);
              }}
            />
            <Spacer size={12} />
            <AdditionalFilterSection
              additionalFilters={userFilter.filter.additionalAttributeSelections ?? []}
              thirdPartyOrganization={thirdPartyOrganization}
              enrichedData={userFilter.enrichedData}
              onEnrichedContractsChange={(enrichedContracts) => {
                setUserFilter((state) => ({
                  ...state,
                  enrichedData: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.enrichedData!,
                    enrichedContracts,
                  },
                }));
              }}
              onChange={(additionalAttributeSelections) => {
                setUserFilter((state) => ({ ...state, filter: { ...state.filter, additionalAttributeSelections } }));
              }}
            />
            <Spacer size={48} />
            <MatchCounter
              userFilter={userFilter.filter}
              badRequest={badRequest}
              onBadRequest={setBadRequest}
              onTryAgain={() => setUserFilter(newFilter)}
            />
          </React.Fragment>
        ) : (
          <ZeroState onSelectPrimaryFilter={() => setPrimaryFilterDialogOpen(true)} />
        )}
        <Spacer size={48} />
        <div>
          <TrackedCdsButton
            trackingName="user_filter_builder_apply"
            css={css`
              ${breakpoints.down('xs')} {
                width: 100%;
                max-width: none;
              }
            `}
            variant="primary"
            disabled={!userFilter.filter.primaryFilter || badRequest}
            onClick={() => {
              const newErrors = validateUserFilter(userFilter.filter);
              if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
              } else {
                const saved = isEqual(userFilter, userFilterFromContext);
                setUserFilterInContext({
                  ...userFilter,
                  filterName: saved ? userFilter.filterName : _t('Unsaved filter selection'),
                  filterId: saved ? userFilter.filterId : 'unsaved-filter-selection',
                });
                if (enableNewLearnersPage) {
                  router.push(
                    programSlug
                      ? `/o/${thirdPartySlug}/admin/programs/${programSlug}/learners?filter=custom`
                      : `/o/${thirdPartySlug}/admin/learners?filter=custom`
                  );
                } else {
                  router.push(
                    programSlug
                      ? getManageProgramLearnersLink(
                          { thirdPartySlug, programSlug },
                          { comingFromFilterBuilder: true },
                          enableNewLearnersPage
                        )
                      : getUsersLink({ thirdPartySlug }, { comingFromFilterBuilder: true })
                  );
                }
              }
            }}
            data-testid="user_filter_builder_apply"
          >
            {_t('Apply')}
          </TrackedCdsButton>
          <Button
            css={css`
              margin: 0 var(--cds-spacing-300) 0;
              ${breakpoints.down('xs')} {
                width: 100%;
                max-width: none;
                margin: var(--cds-spacing-200) 0 0;
              }
            `}
            variant="secondary"
            onClick={() => router.goBack()}
            data-testid="cancel"
          >
            {_t('Cancel')}
          </Button>
          {userFilter.filter.primaryFilter && (
            <Button
              css={css`
                ${breakpoints.down('xs')} {
                  width: 100%;
                  max-width: none;
                  margin: var(--cds-spacing-200) 0 0;
                }
              `}
              disabled={!userFilter.filter.primaryFilter}
              variant="ghost"
              onClick={() => setResetFiltersDialogOpen(true)}
              data-testid="reset-all-filters"
            >
              {_t('Reset all filters')}
            </Button>
          )}
        </div>
        <ConfirmationDialog
          open={resetFiltersDialogOpen}
          heading={_t('Do you want to reset all filters?')}
          confirmationLabel={_t('Reset all filters')}
          onConfirm={() => {
            setUserFilter({
              filterId: 'new-filter',
              filterName: '',
              filter: {},
            });
            setResetFiltersDialogOpen(false);
          }}
          onClose={() => setResetFiltersDialogOpen(false)}
        >
          <Typography2 component="p">
            {_t(
              'This will remove all current filter selections and let you create a new set. It will not affect any saved views.'
            )}
          </Typography2>
        </ConfirmationDialog>
      </Grid>
    </PageGridContainer>
  );
  return (
    <React.Fragment>
      <Header />
      {pageContent}
    </React.Fragment>
  );
};

export const EnrichmentQuery = gql`
  query EnrichmentQuery($programIds: [String!]!) {
    EnterpriseProgramsV1Resource {
      multiGet(ids: $programIds) {
        elements {
          id
          metadata {
            name
          }
        }
      }
    }
  }
`;

type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};
export default compose<Props, PropsFromCaller>(
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'b2b_admin_dashboard',
        page: 'userFilterBuilder',
      },
    };
  }),
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext),

  graphql<
    PropsFromCaller & PropsFromEnterprisePermissionsContext,
    EnrichmentQueryType,
    EnrichmentQueryVariables,
    PropsFromGraphql
  >(EnrichmentQuery, {
    options: ({ enterpriseAdminPermissionsContext }) => ({
      variables: {
        programIds: enterpriseAdminPermissionsContext.getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW'),
      },
    }),
    skip: ({ enterpriseAdminPermissionsContext }) => enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
    props: (props) => {
      return {
        enrichedData: {
          enrichedPrograms:
            props.data?.EnterpriseProgramsV1Resource?.multiGet?.elements.map((element) => ({
              programId: element?.id ?? '',
              programName: element?.metadata.name ?? '',
            })) ?? [],
          enrichedContracts: [],
          enrichedProducts: [],
        },
      };
    },
  })
)(UserFilterBuilder);
